import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { usePrefersReducedMotion } from '@hooks';

/* A deck of photos. Clicking the top card advances the cycle: the front card
   dissolves away, the card behind slides up into its place, and the following
   one fades in behind. The offset card sits exactly where the old static
   border frame used to, so the silhouette of the section is unchanged.

   Every photo is rendered once and positioned by its distance from the current
   index, so a click only changes numbers — the browser interpolates the rest.
   That is what keeps the movement continuous rather than a swap. */
const OFFSET = '14px';
// How far the deck fans apart under the cursor: the front card pulls toward
// the light, the one behind slides the other way, so the gap between them
// opens rather than the pair simply drifting.
const HOVER = '5px';

const Stack = styled.div`
  position: relative;
  width: 100%;
  max-width: 300px;

  /* Without this the grid stretches the stack to the height of the text
     column beside it, and since the hover state lives on this element, every
     empty pixel down to the end of the section counts as hovering the photo.
     Shrinking to the sizer's height makes the hover target the deck itself. */
  align-self: start;

  /* Reserves the space the absolutely-positioned cards occupy, including the
     offset of the card behind. Transparent to the cursor, so the hover state
     belongs to the front card and not to the empty corner beside it. */

  /* Exactly the deck's bounding box: a card is (100% - OFFSET) wide and
     square, and the one behind sits OFFSET lower, so the two together are
     100% tall. Reserving more than that leaves a dead strip that still counts
     as hovering the photo. */
  .sizer {
    width: 100%;
    padding-bottom: 100%;
    pointer-events: none;
  }

  /* Driven from the container, not the card: the card behind is
     pointer-events: none, so it can only learn about the cursor through a
     parent that the front card also lives in. */
     
  &:hover {
    .front {
      transform: translate(calc(${HOVER} * -1), calc(${HOVER} * -1));
    }

    .behind {
      transform: translate(calc(${OFFSET} + ${HOVER}), calc(${OFFSET} + ${HOVER}));
      filter: brightness(0.5) saturate(0.75);
    }
  }

  /* Hold the resting positions rather than clearing the transform — clearing
     it would drop the back card onto the front one. */

  @media (prefers-reduced-motion: reduce) {
    &:hover {
      .front {
        transform: translate(0, 0);
      }

      .behind {
        transform: translate(${OFFSET}, ${OFFSET});
      }
    }
  }

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }
`;

const Card = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  width: calc(100% - ${OFFSET});
  padding: 0;
  border: 0;
  border-radius: var(--border-radius);
  background: transparent;
  overflow: hidden;
  cursor: ${props => (props.isFront ? 'pointer' : 'default')};
  pointer-events: ${props => (props.isFront ? 'auto' : 'none')};

  /* front → flat and lit; behind → offset and dimmed; the rest wait, hidden,
     stacked under the one behind. */
  z-index: ${props => (props.isFront ? 3 : props.isBehind ? 2 : 1)};
  opacity: ${props => (props.isFront || props.isBehind ? 1 : 0)};
  transform: translate(${props => (props.isFront ? '0, 0' : `${OFFSET}, ${OFFSET}`)});

  ${props => (props.isFront ? '' : 'filter: brightness(0.7) saturate(0.4) opacity(0.4);')}

  transition: transform 0.3s var(--easing), opacity 0.3s var(--easing),
    filter 0.3s var(--easing), box-shadow 0.3s var(--easing);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  .gatsby-image-wrapper {
    border-radius: var(--border-radius);
    display: block;
  }

  ${props =>
    props.isFront &&
    `
      box-shadow: 0 10px 30px -15px var(--shadow);

      &:hover,
      &:focus-visible {
        box-shadow: 0 20px 30px -15px var(--shadow);
        outline: none;
      }

      &:focus-visible {
        outline: 2px solid var(--accent);
        outline-offset: 4px;
      }
    `}
`;

const PhotoStack = ({ photos }) => {
  const [index, setIndex] = useState(0);
  const prefersReducedMotion = usePrefersReducedMotion();
  const count = photos.length;

  if (count === 0) {
    return null;
  }

  const advance = () => setIndex(i => (i + 1) % count);

  return (
    <Stack>
      <div className="sizer" />
      {photos.map((photo, i) => {
        // Distance forward from the current card, wrapping around the cycle.
        const position = (i - index + count) % count;
        const isFront = position === 0;
        const isBehind = count > 1 && position === 1;

        return (
          <Card
            key={photo.id}
            type="button"
            className={isFront ? 'front' : isBehind ? 'behind' : 'stowed'}
            isFront={isFront}
            isBehind={isBehind}
            aria-hidden={!isFront}
            tabIndex={isFront ? 0 : -1}
            onClick={isFront ? advance : undefined}
            aria-label={
              isFront && count > 1
                ? `Photo ${index + 1} of ${count}. Show the next one.`
                : undefined
            }
            style={prefersReducedMotion ? { transition: 'none' } : undefined}>
            <GatsbyImage image={photo.image} alt={photo.alt} />
          </Card>
        );
      })}
    </Stack>
  );
};

PhotoStack.propTypes = {
  photos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      image: PropTypes.object.isRequired,
      alt: PropTypes.string,
    }),
  ).isRequired,
};

export default PhotoStack;
